import React, { useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { CopyIcon, WarningIcon } from "../../Icons";
import {
  ContentContainer,
  SuccessIcon,
  ModalTitle,
  ModalSubtitle,
  WarningBox,
  WarningTitle,
  WarningText,
  KeyRow,
  KeyText,
  CopyButton,
  DoneButton,
} from "./CustomerKeyConfirmationModal.styled";

interface CustomerKeyConfirmationModalContentProps {
  clinicName: string;
  customerKey: string;
  onDone: () => void;
}

const CustomerKeyConfirmationModalContent: React.FC<
  CustomerKeyConfirmationModalContentProps
> = ({ clinicName, customerKey, onDone }) => {
  const { t } = useTranslation();
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(customerKey);
      setCopySuccess(true);
    } catch {
      const el = document.createElement("textarea");
      el.value = customerKey;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopySuccess(true);
    }
  };

  return (
    <ContentContainer>
      <SuccessIcon>
        <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
      </SuccessIcon>

      <ModalTitle>
        {t(
          "tenantManagement.customerKey.title",
          "Tenant Created Successfully",
        )}
      </ModalTitle>

      <ModalSubtitle>
        <strong>{clinicName}</strong>{" "}
        {t(
          "tenantManagement.customerKey.subtitle",
          "has been added and is pending activation.",
        )}
      </ModalSubtitle>

      <WarningBox>
        <WarningTitle>
          <WarningIcon width={16} height={16} />
          {t(
            "tenantManagement.customerKey.actionRequired",
            "Action Required: Share Customer Key",
          )}
        </WarningTitle>
        <WarningText>
          {t(
            "tenantManagement.customerKey.actionDescription",
            "You need to copy the Customer Key below and share it manually via email with the main contact person of this tenant organization.",
          )}
        </WarningText>

        <KeyRow>
          <KeyText>{customerKey}</KeyText>
          <CopyButton variant="contained" onClick={handleCopyKey}>
            <CopyIcon width={14} height={14} style={{ marginRight: 4 }} />
            {t("tenantManagement.customerKey.copyKey", "Copy Key")}
          </CopyButton>
        </KeyRow>
      </WarningBox>

      <DoneButton variant="contained" onClick={onDone}>
        {t("common.done", "Done")}
      </DoneButton>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2500}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setCopySuccess(false)}>
          {t(
            "tenantManagement.customerKey.copySuccess",
            "Key copied to clipboard",
          )}
        </Alert>
      </Snackbar>
    </ContentContainer>
  );
};

export { CustomerKeyConfirmationModalContent };
export default CustomerKeyConfirmationModalContent;
